import { useRef, forwardRef, useImperativeHandle, useEffect, useState } from "react";
import DynamicForm, { DynamicFormRef, FormConfig, SELECT_INPUT_TYPE, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import ProviderInvoicesRecibeFormConfig from "./form";
import { CurrencyIdPubSub, CurrencyUpdatePubSubPayload } from "@utils/pubsubs/PubSub";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import { useAppDispatch } from "@/store/hooks";
import { removeFunctionFromObject } from "@utils/Utils";
import { CURRENCIES_SYMBOLS } from "@utils/Constants";
import { DocumentsEntryType } from "@components/dynamic/DocumentStatusList";
import debounce from "debounce";
import { showAlert } from "@utils/Alert";
import { ProviderInvoicesRecibeDataType } from "@/formTypes/ProviderInvoicesRecibeFormDataType";
import { buildAbonoDocumentOptions } from "@utils/ProviderRecibeAbono";

type ProviderInvoicesRecibeFormProps = {
  onProviderInvoiceRecibeSubmit: (data: ProviderInvoicesRecibeDataType) => void;
  onRecibeDateChange: (projectDate: string) => void;
  defaultData?: ProviderInvoicesRecibeDataType;
  innerForm?: boolean;
  recibeTotal: number;
  selectedDocuments: DocumentsEntryType[],
  /**
   * Moneda de los documentos del recibo. Se recibe ya resuelta para poder renderizar el
   * simbolo del input de dinero en el primer render: `DynamicForm` crea el addon al montar
   * el input y no lo reemplaza si el simbolo llega despues, asi que esperar el
   * `CurrencyIdPubSub` (que hace un `CurrencyApi.get`) dejaba el `$` por defecto.
   */
  recibeCurrency?: CurrencyDataType,
};

const resolveCurrencySymbol = (currency?: CurrencyDataType): string =>
  CURRENCIES_SYMBOLS[Number(currency?.currency_id)] || "";

// Usamos forwardRef para permitir ref externo
const ProviderInvoicesRecibeForm = forwardRef<DynamicFormRef, ProviderInvoicesRecibeFormProps>(
  (props, ref) => {
    const { onProviderInvoiceRecibeSubmit, innerForm = false, defaultData,
      selectedDocuments, recibeTotal, onRecibeDateChange, recibeCurrency } = props;
    const dispatch = useAppDispatch();
    const [currencySymbol, setCurrencySymbol] = useState<string>(() => resolveCurrencySymbol(recibeCurrency));
    const [isRecibeInFull, setRecibeInFull] = useState<boolean>(true);

    const internalRef = useRef<DynamicFormRef>(null);
    useImperativeHandle(ref, () => internalRef.current as DynamicFormRef, []);


    useEffect(() => {
      const unsubscribe = CurrencyIdPubSub.subscribe(setFormCurrency);
      return () => {
        unsubscribe();
      };
    }, []);

    // Al abrir el offcanvas con otra seleccion el monto vuelve a ser el saldo completo,
    // asi que el abono arranca oculto aunque el recibo anterior haya sido parcial.
    useEffect(() => {
      setRecibeInFull(true)
    }, [recibeTotal]);

    useEffect(() => {
      const symbol = resolveCurrencySymbol(recibeCurrency);
      if (symbol) {
        setCurrencySymbol(symbol)
      }
    }, [recibeCurrency?.currency_id]);

    const setFormCurrency = async (payload: CurrencyUpdatePubSubPayload) => {
      const { currencyId } = payload;
      const priceData = await CurrencyApi.get(currencyId);
      if (priceData.currency_id) {
        dispatch(setCurrency(removeFunctionFromObject(priceData)));

        setCurrencySymbol(CURRENCIES_SYMBOLS[priceData.currency_id])
        if (internalRef) {
          internalRef.current?.setFormValue("multiple",
            {
              currencyId,
              exchange_rate: priceData.exchange_rate

            }, "multipleData")
        }
      }
    }

    const getFormConfig = (): FormConfig => {
      const baseConfig: FormConfig = currencySymbol
        ? setCurrencySymbolForMoneyInputs(ProviderInvoicesRecibeFormConfig, currencySymbol)
        : ProviderInvoicesRecibeFormConfig;

      // Se clona la estructura antes de tocarla: la config es un modulo compartido y
      // mutarla filtra el tipo del campo del abono entre montajes del formulario.
      const config: FormConfig = {
        ...baseConfig,
        sections: baseConfig.sections.map(section => ({
          ...section,
          fields: section.fields.map(row => row.map(field => ({ ...field })))
        }))
      };

      const abonoInput = config.sections.flatMap(s => s.fields).flat().find(f => f.name === "factura_abono")
      if (abonoInput) {
        if (isRecibeInFull) {
          abonoInput.type = "hidden"
        } else {
          abonoInput.type = SELECT_INPUT_TYPE
          abonoInput.options = buildAbonoDocumentOptions(selectedDocuments)
        }
      }
      return config
    }

    const checkReciboFullPayment = debounce((amount: string) => {
      const newRecibeAmount = Number(amount);
      if (newRecibeAmount > recibeTotal) {
        showAlert("danger", "El monto total del recibo no puede ser superior al monto total de los documentos")
        internalRef.current?.setFormValue("total", recibeTotal, "decimal")
        setRecibeInFull(true)
      } else {
        setRecibeInFull(newRecibeAmount == recibeTotal)
      }
    }, 300);

    const onInputChange = (name: keyof ProviderInvoicesRecibeDataType, value: unknown) => {
      if (name == "total") {
        checkReciboFullPayment(`${value}`)
      } else if (name == "created") {
        onRecibeDateChange(`${value}`)
      }
    }

    return (
      <DynamicForm<ProviderInvoicesRecibeDataType>
        externalFormState={defaultData}
        ref={internalRef}
        innerForm={innerForm}
        formConfig={getFormConfig()}
        onInputChange={onInputChange}
        onSubmit={onProviderInvoiceRecibeSubmit}
      />
    );
  }
);

export default ProviderInvoicesRecibeForm;
